<template>
  <div :class="['layout-container', $style.container]">
    <Skeleton v-if="isLoading" />
    <template v-else>
      <ToastMessage />
      <OverviewHeader :showAddButton="!isEmpty" />
      <MyProperties
        v-if="!isEmpty"
        :propertiesData="propertiesData!"
        @onRemoveProperty="handleRemoveProperty"
        @onLastPropertyRemoved="handleLastPropertyRemoved"
      />
      <EmptyStateView v-else data-testid="empty-state-view"></EmptyStateView>
    </template>
  </div>
</template>

<script lang="ts" setup>
import OverviewHeader from '@/components/property-overview-page/OverviewHeader/OverviewHeader.vue';

import MyProperties from '@/components/property-overview-page/MyProperties/MyProperties.vue';
import Skeleton from '@/pages/PropertyOverview/Skeleton.vue';
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';

import { useI18n } from 'vue-i18n';

import ToastMessage from '@/components/common/ToastMessage/ToastMessage.vue';
import { useTrackingVirtualPageView } from '@/composables/useTrackingVirtualPageView';

import { trackUserAuthentication } from '@/tracking/tracking';

import EmptyStateView from '@/components/property-overview-page/EmptyStateView/EmptyStateView.vue';
import { isServer } from '@/helpers/Platform/Platform';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { SOURCE_QUERY_STRING_VALUES, VALUATION_FORM_PATH } from '@/router/routes/constants';
import { TRACKING_LABEL } from '@/tracking/types';
import { useAuth0, User } from '@auth0/auth0-vue';
import { useHead } from '@unhead/vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

const { locale, t } = useI18n();

const isLoading = ref<boolean>(true);
const propertiesData = ref<PropertyValuationResult[] | null>(null);
const getPageTitle = () => t('metaInfo.overview.title') as string;
const userDetails = ref<User | undefined>(undefined);
const { commit, getters, dispatch } = useStore();
const { push } = useRouter();
const { query } = useRoute();

const FETCH_USER_PROPERTIES_ACTION = 'common/fetchUserProperties';
const { trackVirtualPageView } = useTrackingVirtualPageView();

const isUserFirstTimeLogin = computed(() => getters['common/getIsUserFirstTimeLogin']);

useHead({
  title: getPageTitle(),
  htmlAttrs: {
    lang: locale.value,
  },
});

const handleRemoveProperty = async () => {
  propertiesData.value = await dispatch(FETCH_USER_PROPERTIES_ACTION, true);
};

const handleLastPropertyRemoved = async () => {
  propertiesData.value = await dispatch(FETCH_USER_PROPERTIES_ACTION, true);
};

const isEmpty = computed<boolean>(() => !propertiesData.value || propertiesData.value.length === 0);

onBeforeMount(async () => {
  propertiesData.value = await dispatch(FETCH_USER_PROPERTIES_ACTION, true);
  isLoading.value = false;

  if (!propertiesData.value?.length && query?.source === SOURCE_QUERY_STRING_VALUES.HO_LANDING) {
    push(VALUATION_FORM_PATH[locale.value]);
  }
});

onMounted(() => {
  const { user } = useAuth0();
  userDetails.value = user.value;
});

const username = computed(() => {
  return getters['common/getUsername'];
});

watch(
  () => propertiesData.value,
  () => {
    if (username.value && !isServer()) {
      callTracking();
    }
  },
  { once: true },
);

const callTracking = () => {
  const label = isEmpty.value
    ? TRACKING_LABEL.EMPTY_PROPERTY_OVERVIEW
    : TRACKING_LABEL.PROPERTY_OVERVIEW;
  const propertyData = propertiesData.value ?? [];

  const hasProperties = propertyData.length > 0;

  trackVirtualPageView({
    subType: label,
    locale: locale.value,
    moreParams: {
      h_login_status_035: 'loggedin',
      h_client_login_user_id_036: username.value,
      h_user_group_039: hasProperties ? 'is_how_user' : 'not_how_user',
      h_list_items_loaded_285: propertyData.length,
      h_list_items_loaded_088: propertyData.map((item) => item.leadId),
    },
  });

  if (isUserFirstTimeLogin.value) {
    trackUserAuthentication({
      h_login_location_049: 'property_valuation_dashboard',
      h_login_provider_034: userDetails.value?.sub?.split('|')[0].split('-')[0] ?? '',
      h_client_login_user_id_036: username.value,
    });
    commit('common/setIsUserFirstTimeLogin', false);
  }
};
defineExpose({
  handleLastPropertyRemoved,
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;
$container-min-height: 752px;

.container[class*='layout-container'] {
  padding: $space-150 $space-100;

  @include grid-media-query(sm) {
    padding: $space-150 $space-400;
  }

  @include grid-media-query(md) {
    min-height: $container-min-height;
    padding: $space-150 $space-200;
  }
}

.header {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  padding-bottom: $space-200;
}
</style>
